import React, { useState } from 'react'

import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import moment from 'moment'

import Heading from 'presentation/shared/components/Heading'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { Patient } from 'domain/entities/patient-model'
import { cpfMask } from 'presentation/utils/masks'
import SelectField from 'presentation/shared/components/SelectField'
import { Hospital } from 'domain/entities/hospital-model'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import Button from 'presentation/shared/components/Button'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { getDocumentsLabelLocationByEnum } from 'presentation/utils/document-types-location'
import { ReactComponent as DownloadIcon } from 'presentation/assets/icons/download-icon.svg'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import getAgeByBirthdayDate from 'common/utils/getAgeByBirthdayDate'

import * as S from './styles'
import { SolicitationCancelOrderModal } from './SolicitationCancelOrderModal'
import { ReasonsCancellationModel } from 'domain/entities/reasons-cancellation'
import { changesFirstLetterDocuments } from 'common/utils/changesFirstLetterDocuments'

type Props = {
  loadPatientInfo: Patient
  onlyVisualization: boolean
  surgicalOrder?: SurgicalOrderModel
  hospitals: Hospital[]
  loadSurgicalOrderDocument?: LoadSurgicalOrderDocument
  requester?: string
  reasons?: ReasonsCancellationModel[]
} & WithLoadingProps

export const SolicitationCancelOrderForm = WithLoading(
  ({
    loadPatientInfo,
    hospitals,
    surgicalOrder,
    loadSurgicalOrderDocument,
    requester,
    reasons,
    setIsLoading
  }: Props) => {
    const [showCancelOrderModal, setShowCancelOrderModal] =
      useState<boolean>(false)
    const [onlyVisualization] = useState(true)
    const birthDay = moment(loadPatientInfo.birthday).format('DD/MM/YYYY')
    const solicitationDate = moment
      .utc(surgicalOrder?.cancellation_request?.created_at)
      .format('DD/MM/YYYY - HH:mm')
    const age = getAgeByBirthdayDate(birthDay)

    const { handleSubmit } = useFormik({
      initialValues: {
        cpf: loadPatientInfo.cpf,
        name: loadPatientInfo.name,
        gender: loadPatientInfo.gender,
        phone: loadPatientInfo.phone,
        email: loadPatientInfo.email,
        birthday: birthDay
      },
      enableReinitialize: true,
      onSubmit: async () => null
    })

    const handleDownlodDocument = async (document_id: number, type: string) => {
      try {
        if (surgicalOrder?.surgical_order_id) {
          setIsLoading(true)
          const surgicalOrderDocument = await loadSurgicalOrderDocument?.load(
            surgicalOrder?.surgical_order_id,
            document_id
          )
          if (surgicalOrderDocument) {
            downloadFileFromBlob(
              surgicalOrderDocument.data,
              surgicalOrderDocument.contentType,
              getDocumentsLabelLocationByEnum(type)
            )
          }
        }
      } catch (error) {
        toast.error('Falha ao realizar o download do arquivo')
      } finally {
        setIsLoading(false)
      }
    }

    const units = hospitals.map(({ name, friendly_name, hospital_id }) => ({
      label: friendly_name || name,
      value: hospital_id
    }))

    const optionUnits = [...units, { label: '', value: 0 }]

    return (
      <>
        <form onSubmit={handleSubmit}>
          <Heading as="h2" size={'medium'}>
            Dados do paciente
          </Heading>

          <S.Wrapper>
            <S.InputsContainer>
              <TextFieldNew
                key={'cpf'}
                label={'CPF'}
                name={'cpf'}
                mask={cpfMask}
                disabled={onlyVisualization}
                value={loadPatientInfo.cpf}
              />
              <TextFieldNew
                key={'name'}
                label={'Paciente'}
                name={'name'}
                disabled={onlyVisualization}
                value={loadPatientInfo.name}
              />
              <TextFieldNew
                key={'birthday'}
                label={'Data de nascimento'}
                name={'birthday'}
                disabled={onlyVisualization}
                value={birthDay}
              />
              <TextFieldNew
                key={'age'}
                label={'Idade'}
                name={'age'}
                disabled={onlyVisualization}
                value={`${age} anos`}
              />
            </S.InputsContainer>
            <S.InputsContainer>
              <TextFieldNew
                key={'gender'}
                label={'Sexo'}
                name={'gender'}
                disabled={onlyVisualization}
                value={loadPatientInfo.gender}
              />
              <TextFieldNew
                key={'phone'}
                label={'Celular'}
                name={'phone'}
                disabled={onlyVisualization}
                value={loadPatientInfo.phone}
              />
              <TextFieldNew
                key={'landlinePhone'}
                label={'Telefone fixo'}
                name={'landlinePhone'}
                disabled={onlyVisualization}
                value={loadPatientInfo.landlinePhone}
              />
              <TextFieldNew
                key={'email'}
                label={'E-mail'}
                name={'email'}
                disabled={onlyVisualization}
                value={loadPatientInfo.email}
              />
            </S.InputsContainer>
          </S.Wrapper>
        </form>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Unidade de realização
          </Heading>
          <SelectField
            label="Hospital *"
            name="unit"
            items={optionUnits}
            value={surgicalOrder?.hospital_id}
            style={{ width: 300 }}
            disabled
          />
        </S.Container>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Dados do convênio
          </Heading>
        </S.Container>

        <S.InputsContainer>
          <TextFieldNew
            label="Convênio"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthInsuranceName}
          />
          <TextFieldNew
            label="Plano de saúde"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthPlanName}
          />
          <TextFieldNew
            label="Número da carteira"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthCard}
          />
          <TextFieldNew
            label="Validade da carteira"
            disabled
            defaultValue={
              surgicalOrder?.healthInsurance?.validThru
                ? moment(surgicalOrder?.healthInsurance?.validThru).format(
                    'DD/MM/YYYY'
                  )
                : ''
            }
          />
          <TextFieldNew
            label="Empresa"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.company}
          />
        </S.InputsContainer>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Anexos
          </Heading>
        </S.Container>

        <S.BgContainer>
          <S.Documents>
            {surgicalOrder?.documents?.map((document) => (
              <S.Document key={document.document_id}>
                <Heading
                  as="h2"
                  size="small"
                  style={{ flex: 1, fontWeight: 400 }}
                >
                  {changesFirstLetterDocuments(document.name)}
                </Heading>
                <Button
                  variant="download"
                  fullWidth
                  onClick={() =>
                    handleDownlodDocument(document.document_id, document.type)
                  }
                  disabled={!onlyVisualization}
                  type="button"
                  style={{ width: 120, padding: '15px' }}
                >
                  <DownloadIcon className="icon" />
                  Download
                </Button>
              </S.Document>
            ))}
          </S.Documents>
        </S.BgContainer>

        <S.Container>
          <Heading as="h2" size="small" style={{ flex: 1 }}>
            Justificativa de solicitação de cancelamento
          </Heading>
          <S.CancelInputsContainers>
            <TextFieldNew
              key={'solicitation'}
              label={'Solicitante'}
              name={'solicitation'}
              disabled={onlyVisualization}
              value={requester}
            />
            <TextFieldNew
              key={'soliciationDate'}
              label={'Data e horário da solicitação'}
              name={'soliciationDate'}
              disabled={onlyVisualization}
              value={solicitationDate}
            />
            <TextAreaNew
              key={'applicantsJustification'}
              rows={3}
              scrolling="auto"
              label={'Justificativa do solicitante'}
              name={'applicantsJustification'}
              disabled={onlyVisualization}
              value={surgicalOrder?.cancellation_request?.reason}
            />
            <TextAreaNew
              key={'comment'}
              rows={3}
              scrolling="auto"
              label={'Comentário'}
              name={'comment'}
              disabled={onlyVisualization}
              value={
                surgicalOrder?.cancellation_request?.comments?.[0]?.comment
              }
            />
          </S.CancelInputsContainers>
        </S.Container>

        {showCancelOrderModal && (
          <SolicitationCancelOrderModal
            solicitationCancelOrder={
              surgicalOrder?.cancellation_request?.surgical_cancellation_id
            }
            reasons={reasons}
            surgicalOrder={surgicalOrder}
            handleClose={() => setShowCancelOrderModal(false)}
          />
        )}

        <S.Container style={{ position: 'relative', marginBottom: 100 }}>
          <Button
            fullWidth
            onClick={() => {
              if (
                !surgicalOrder?.cancellation_request?.surgical_cancellation_id
              ) {
                toast.error(
                  'Este pedido não possui uma solicitação de cancelamento ativa.'
                )
                return
              }
              setShowCancelOrderModal(true)
            }}
            disabled={!onlyVisualization}
            type="button"
            style={{
              width: 245,
              position: 'absolute',
              right: 0,
              fontSize: '16px'
            }}
          >
            Cancelar pedido cirúrgico
          </Button>
        </S.Container>
      </>
    )
  }
)
